import React, { useEffect, useState } from "react";

const WordSearchEditComponent = ({ game_id, date, onCancel, challenge }) => {
  const CURRENT_SELECTED_CHALLENGE = challenge;
  const [challengeDict, setChallengeDict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!game_id || !date) return;
    setLoading(true);
    setError(null);
    const url = `${process.env.REACT_APP_CMC_API}/statistics?method=getDailyChallengeComplex&gameid=${game_id}&date=${date}`;
    fetch(url, {
      headers: { Connection: "close" },
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch complex challenge data");
        return response.json();
      })
      .then((json) => {
        if (json && json.challenge_name && Array.isArray(json.challenge_name)) {
          const dict = {};
          for (let i = 0; i < json.challenge_name.length; i++) {
            const name = json.challenge_name[i];
            if (!dict[name]) {
              dict[name] = {
                game_id: json.game_id?.[i],
                challenge_name: name,
                variants: {},
              };
            }
            const variant_name = json.variant_name?.[i];
            const variant_id = json.variant_id?.[i];
            const challenge_data = json.challenge_data?.[i];

            if (variant_name) {
              dict[name].variants[variant_name] = {
                variant_id: variant_id,
                challenge_data: challenge_data,
              };
            }
          }
          setChallengeDict(dict);
        } else {
          setChallengeDict(null);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [game_id, date]);
  
  const selectedChallenge = challengeDict?.[CURRENT_SELECTED_CHALLENGE];
  const challengeData = selectedChallenge?.variants?.challenge?.challenge_data;

  // Parse the challenge data
  let puzzleData = null;
  let grid = [];
  let words = [];
  let title = "";
  let creator = "";
  let description = "";
  let width = 0;
  let height = 0;

  if (challengeData) {
    try {
      puzzleData = JSON.parse(challengeData);
      if (puzzleData) {
        title = puzzleData.title || "Untitled";
        creator = puzzleData.creator || "Unknown";
        description = puzzleData.description || "";
        width = puzzleData.width || 0;
        height = puzzleData.height || 0;
        words = puzzleData.words || [];
        
        // Create grid from cells data
        if (puzzleData.cells && width > 0 && height > 0) {
          grid = Array(height).fill(null).map(() => Array(width).fill(''));
          puzzleData.cells.forEach(cell => {
            if (cell.x > 0 && cell.x <= width && cell.y > 0 && cell.y <= height) {
              grid[cell.y - 1][cell.x - 1] = cell.solution;
            }
          });
        }
      }
    } catch (e) {
      console.error("Failed to parse challenge data:", e);
    }
  }
  
  if (loading) return <div>Loading complex challenge data...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div className="p-3 rounded-xl mt-2">
        {/* Puzzle Info */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-neutral-800 rounded-md">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">{title}</h3>
            <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                {creator && <div><strong>Creator:</strong> {creator}</div>}
                {description && <div><strong>Theme:</strong> {description}</div>}
                <div><strong>Grid Size:</strong> {width} × {height}</div>
                <div><strong>Words:</strong> {words.length}</div>
            </div>
        </div>

        {/* Grid Preview */}
        <div className="mb-4">
            <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">Word Search Grid</h4>
            <div className="bg-gray-100 dark:bg-neutral-800 p-3 rounded-md flex justify-center">
                {grid.length > 0 ? (
                    <div className="inline-block border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                        {grid.map((row, rowIndex) => (
                            <div key={rowIndex} className="flex">
                                {row.map((cell, colIndex) => (
                                    <div
                                        key={`${rowIndex}-${colIndex}`}
                                        className="w-6 h-6 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-xs font-bold bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                                    >
                                        {cell || ' '}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-neutral-500 text-center">No grid data available.</p>
                )}
            </div>
        </div>

        {/* Words List */}
        <div className="mb-4">
            <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">Words to Find</h4>
            <div className="max-h-48 overflow-y-auto border dark:border-neutral-700 p-3 rounded-md bg-gray-50 dark:bg-neutral-800">
                {words.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-2 gap-2">
                        {words.map((word, index) => (
                            <div key={index} className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-700 p-2 rounded border">
                                {word.solution}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-neutral-500 text-center">No words available.</p>
                )}
            </div>
        </div>

        {/* Clues (if available) */}
        {puzzleData?.clues && puzzleData.clues.length > 0 && (
            <div className="mb-4">
                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">Clues</h4>
                <div className="max-h-48 overflow-y-auto border dark:border-neutral-700 p-3 rounded-md bg-gray-50 dark:bg-neutral-800">
                    <div className="space-y-2">
                        {puzzleData.clues.map((clue, index) => (
                            <div key={index} className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>{clue.wordId}:</strong> {clue.clueText}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}
      
        <div className="flex justify-end gap-2 mt-4">
          <button title="Close" onClick={onCancel} className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 p-2 rounded-md">
            <svg width="20" height="20" fill="none" stroke="#F8A5A5" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
    </div>
  );
};

export default WordSearchEditComponent; 